import './store-finder.scss';
import './store-finder-logos.js';
import { StoreFinderMap } from './store-finder-map';
import AjaxLoad from './../../utilities/js/ajax-load';
import { smoothScrollTo, getOffset, createElementFromHTML } from '../../utilities/js/helper';
import { Loader } from '@googlemaps/js-api-loader';
import Swiper from 'swiper';
import { Mousewheel, Navigation, Pagination } from 'swiper/modules';

class StoreFinder {
    constructor ($container) {
        this.$storeFinder = $container;

        this.$form = this.$storeFinder.querySelector('form');
        this.$locationInput = this.$storeFinder.querySelector('[data-store-finder="location"]');
        this.$inputDeleteButton = this.$storeFinder.querySelector('[data-store-finder="delete-input"]');
        this.lastLocationValue = '';
        this.autoScroll = false;
        this.$submitButton = this.$storeFinder.querySelector('[data-form-submit]');
        this.inputLAT = this.$storeFinder.querySelector('[data-location="lat"]');
        this.inputLNG = this.$storeFinder.querySelector('[data-location="lng"]');
        this.inputFilter = this.$storeFinder.querySelector('[data-location="filter"]');
        this.countryRestriction = { country: ['DE'] };
        this.foundEvent = new CustomEvent('changeInput', {
            view: window,
            bubbles: true,
            cancelable: false
        });

        this.autocomplete = null;
        this.$productNotification = this.$storeFinder.querySelector('[data-store-finder="product-notification"]');
        this.$noResult = this.$storeFinder.querySelector('[data-store-noresult]');
        this.$storeResult = document.querySelector('[data-store-finder="results"]');
        this.$storeResultItems = this.$storeResult.querySelectorAll('[data-store-finder="store"]');
        this.$resultAnchor = document.getElementById('results');
        this.$radius = document.querySelector('[data-select][id="radius"]');

        this.$storeResultText = this.$storeFinder.querySelector('[data-store-finder="resulttext"]');
        this.$storeResultTextWrapper = this.$storeFinder.querySelector('[data-store-resulttext-wrapper]');
        this.$storeFinderMap = this.$storeFinder.querySelector('[data-store-finder="map"]');
        this.$storeFinderStoreUrl = this.$storeFinderMap.getAttribute('data-store-finder-store-url');
        this.$storeFinderMapContainer = this.$storeFinder.querySelector('[data-store-finder="mapcontainer"]');

        this.$productSelectionSwitch = document.querySelector('input[id="product-selection-switch"]');
        this.$productSelections = this.$storeFinder.querySelector('[data-store-finder="selected-products"]');
        this.$productSelectionsItems = null;
        this.productSelectionsSlider = null;

        this.$products = document.querySelector('[data-productlist="root"]');
        this.$productInputs = this.$products.querySelectorAll('input[type="checkbox"]');
        this.selectedProducts = '';
        this.selectedCategories = '';
        this.$selectionActions = this.$storeFinder.querySelector('[data-store-finder="selection-actions"]');
        this.$selections = this.$storeFinder.querySelector('[data-store-finder="selections"]');

        this.$locationContainer = this.$storeFinder.querySelector('[data-store-finder="location-container"]');
        this.$locationScrollPosition = this.$storeFinder.querySelector('[data-store-finder="location-scroll-pos"]');
        this.$storeFinderMobileListSwitch = this.$storeFinder.querySelector('input[id="storefinder-list"]');

        this.storeMap = null;

        this.foundLocation = false;
        window.storeSlider = null;

        this.activeRequest = false;

        this.fixedLocationAdjustment = 200;

        this.googleAPIKey = googleMapsApiKey; // eslint-disable-line
    }

    initialize () {
        if (this.$storeFinderMap && this.googleAPIKey !== '') {
            const loader = new Loader({
                apiKey: this.googleAPIKey,
                version: 'quarterly',
                libraries: ['places']
            });

            loader.load().then(() => {
                if (typeof google !== 'undefined') {
                    this.storeMap = new StoreFinderMap(this.$storeFinderMap, {
                        centerLat: this.$storeFinderMap.getAttribute('data-lat'),
                        centerLng: this.$storeFinderMap.getAttribute('data-lng')
                    });

                    this.setEvents();
                    this.autoComplete();
                    this.storeMap.setVisualRadius(this.inputLAT.value, this.inputLNG.value, this.$radius.value);
                } else {
                    const $waitForGoogle = setInterval(() => {
                        if (typeof google !== 'undefined') {
                            clearInterval($waitForGoogle);
                            this.storeMap = new StoreFinderMap(this.$storeFinderMap, this, {
                                centerLat: this.$storeFinderMap.getAttribute('data-lat'),
                                centerLng: this.$storeFinderMap.getAttribute('data-lng')
                            });

                            this.setEvents();
                            this.autoComplete();
                            this.storeMap.setVisualRadius(this.inputLAT.value, this.inputLNG.value, this.$radius.value);
                        }
                    }, 100);
                }
            });
        }

        this.checkSelectedProducts();

        if (this.$storeResultItems) {
            if (this.$storeResultItems.length > 0) {
                this.initSlider();
            }
        }

        if (this.$productSelections) {
            this.initSelectedProductsSlider();
        }
    }

    scrollTo ($target) {
        smoothScrollTo(0, getOffset($target).top, 500);
    }

    initSlider () {
        this.$storeResult = document.querySelector('[data-store-finder="results"]');
        this.$storeResultItems = this.$storeResult.querySelectorAll('[data-store-finder="store"]');

        Swiper.use([Mousewheel]);
        Swiper.use([Pagination]);

        window.storeSlider = new Swiper(this.$storeResult, {
            slidesPerView: 'auto',
            direction: 'vertical',
            autoHeight: true,
            freeMode: {
                enabled: true,
                sticky: true
            },
            mousewheel: {
                forceToAxis: true
            },
            pagination: {
                type: 'progressbar',
                el: document.querySelector('[data-store-finder-result-pagination]')
            },
            observer: true,
            watchSlidesProgress: true,
            spaceBetween: 40,
            speed: 500
        });

        this.setSliderEvents();
    }

    initSelectedProductsSlider () {
        this.$productSelectionsItems = this.$productSelections.querySelectorAll('[data-store-finder-selection]');

        Swiper.use([Navigation]);

        this.productSelectionsSlider = new Swiper(this.$productSelections, {
            slidesPerView: 'auto',
            navigation: {
                nextEl: this.$productSelections.querySelector('.store-finder__selections-next'),
                prevEl: this.$productSelections.querySelector('.store-finder__selections-prev')
            },
            watchSlidesProgress: true,
            spaceBetween: 26,
            speed: 500
        });

        this.$productSelectionsItems.forEach((element) => {
            element.addEventListener('click', () => {
                const productId = element.getAttribute('data-store-finder-selection');

                const checkbox = this.$products.querySelector('input[value="' + productId + '"]');
                if (checkbox) {
                    checkbox.checked = false;
                }

                const url = new URL(window.location.href);
                if (url.searchParams.has('productId')) {
                    const remaining = url.searchParams.get('productId').split(',').filter(id => id !== productId);
                    if (remaining.length === 0) {
                        url.searchParams.delete('productId');
                    } else {
                        url.searchParams.set('productId', remaining.join(','));
                    }
                    history.replaceState(null, '', url.toString());
                }

                element.remove();
                this.ajaxRequest(this.$form.getAttribute('action'), false);
            });
        });
    }

    checkSelectedProducts () {
        this.selectedProducts = '';
        const urlProduct = window.location.href.split('productId=')[1];

        if (urlProduct) {
            this.selectedProducts = urlProduct;
        }

        const selected = this.$products.querySelectorAll('input[type="checkbox"]:checked');

        selected.forEach((input) => {
            if (this.selectedProducts === '') {
                this.selectedProducts = input.value;
            } else {
                this.selectedProducts += ',' + input.value;
            }
        });
    }

    checkSelectedCategories () {
        this.selectedCategories = '';

        const selected = document.querySelectorAll('input[name="filterHauptkategorie[]"]:checked');

        selected.forEach((input) => {
            if (this.selectedCategories === '') {
                this.selectedCategories = input.value;
            } else {
                this.selectedCategories += ',' + input.value;
            }
        });
    }

    setSliderEvents () {
        this.$storeResultItems.forEach((store) => {
            const id = parseInt(store.getAttribute('data-store-id'));

            store.addEventListener('click', (e) => {
                const $productLink = store.querySelector('[data-store-finder="product-results"]');
                const linkIsClicked = !!($productLink && (e.target === $productLink || $productLink.contains(e.target)));

                if (!linkIsClicked || !store.classList.contains('is--active')) {
                    store.classList.add('is--active');
                    this.storeMap.setActiveMarkerWithId(id);
                }

                if (linkIsClicked) {
                    this.getStoreProducts(id, this.storeMap.activeInfoBoxMarker.handelspartnerId, (storeProducts) => {
                        this.setStoreProducts(storeProducts, id);
                    });
                }
            });
        });
    }

    destroySlider (slider, callback) {
        if (slider !== null) {
            slider.destroy(true, true);
            slider = null;
        }

        callback();
    }

    dispatchForCode (event, callback) {
        let code;

        if (event.key !== undefined) {
            code = event.key;
        } else if (event.keyIdentifier !== undefined) {
            code = event.keyIdentifier;
        } else if (event.keyCode !== undefined) {
            code = event.keyCode;
        }

        callback(code);
    }

    autoComplete () {
        this.autocomplete = new google.maps.places.Autocomplete( // eslint-disable-line
            /** @type {!HTMLInputElement} */ (
                this.$locationInput), {
                // types: ['(regions)'],
                componentRestrictions: this.countryRestriction
            });

        this.autocomplete.addListener('place_changed', () => {
            const place = this.autocomplete.getPlace();

            const filteredArray = place.address_components.filter((address_component) => { // eslint-disable-line
                return address_component.types.includes('country');
            });

            if (place.geometry !== undefined) {
                if (this.inputLAT && this.inputLNG) {
                    this.inputLAT.value = place.geometry.location.lat();
                    this.inputLNG.value = place.geometry.location.lng();
                }

                this.foundLocation = true;
                this.lastLocationValue = encodeURI(this.$locationInput.value);
                this.$form.dispatchEvent(this.foundEvent);
            } else {
                this.foundLocation = false;
            }
        });

        google.maps.event.addDomListener(this.$locationInput, 'keydown', (e) => { // eslint-disable-line
            if (!document.querySelector('.pac-item-selected')) {
                this.dispatchForCode(e, (code) => {
                    if (code === 'Enter' && !e.triggered) {
                        this.selectFirstLocation(true);
                    }
                });
            }
        });

        this.$form.addEventListener('changeInput', (e) => {
            this.$submitButton.classList.remove('is--disabled');
            // this.checkBeforeSubmit(e);
        });
    }

    // Update usercentrics icon position
    updateUsercentrics () {
        const $usercentrics = document.getElementById('usercentrics-root');

        if ($usercentrics) {
            const $shadowRoot = $usercentrics.shadowRoot;
            const $styleTag = $shadowRoot.querySelector('[custom-style]');
            let space;

            if (window.innerWidth < 768 && this.$locationScrollPosition.classList.contains('is--fixed')) {
                space = 175;
            } else {
                space = 0;
            }

            const styleInformation = `\n button[data-testid] { margin-bottom: ${space}px; transition: margin-bottom 0.3s cubic-bezier(0.25, 1, 0.5, 1)`;

            if (!$styleTag) {
                const $styleTag = document.createElement('style');
                $styleTag.setAttribute('custom-style', '');
                $styleTag.innerHTML = styleInformation;
                $shadowRoot.appendChild($styleTag);
            } else {
                $styleTag.innerHTML = styleInformation;
            }
        }
    }

    positionPacContainer (pac, fixed) {
        window.setTimeout(() => {
            const top = this.$locationInput.getBoundingClientRect().top + this.$locationInput.clientHeight + window.scrollY;
            const left = this.$locationInput.getBoundingClientRect().left;
            pac.style.setProperty('--left', `${left}px`);
            pac.style.setProperty('--top', fixed ? 'auto' : `${top}px`);
        }, 250);
    }

    setEvents () {
        this.$radius.addEventListener('change', () => {
            this.$submitButton.classList.remove('is--disabled');
            this.ajaxRequest(this.$form.getAttribute('action'), false);
        });

        this.$products.addEventListener('change', (e) => {
            if (e.target.value !== '') {
                this.$submitButton.classList.remove('is--disabled');
            }
        });

        this.$productSelectionSwitch.addEventListener('change', () => {
            if (this.$productSelectionSwitch.checked === false) {
                this.$submitButton.classList.remove('is--disabled');
            }
        });

        this.$inputDeleteButton.addEventListener('click', () => {
            this.$locationInput.value = '';
            this.$submitButton.classList.add('is--disabled');
            this.$inputDeleteButton.classList.remove('is--visible');
        });

        this.$locationInput.addEventListener('focus', () => {
            this.checkForAutocompleteResults();
        });

        this.$locationInput.addEventListener('blur', () => {
            this.$locationInput.parentElement.classList.remove('has--focus');
        });

        this.$locationInput.addEventListener('keyup', () => {
            if (this.$locationInput.value.length >= 1) {
                this.$inputDeleteButton.classList.add('is--visible');
                this.checkForAutocompleteResults();
            } else {
                this.$locationInput.parentElement.classList.remove('has--focus');
                this.$inputDeleteButton.classList.remove('is--visible');
            }
        });

        this.$form.addEventListener('submit', (e) => {
            e.preventDefault();
            e.stopPropagation();

            this.checkBeforeSubmit(e);
        });

        window.addEventListener('store_products', (e) => {
            const eventDetails = e.detail;

            this.getStoreProducts(eventDetails.id, eventDetails.handelspartnerId, (storeProducts) => {
                this.setStoreProducts(storeProducts, eventDetails.id);
                window.storeSlider.update();
            });
        });

        window.eventScroller.customFunctions.push(() => {
            const $pac = document.querySelector('.pac-container');
            if (!this.$productSelectionSwitch.checked) {
                if (!this.$locationScrollPosition.classList.contains('is--fixed')) {
                    if (window.scrollPosition > getOffset(this.$locationScrollPosition).top && (window.scrollPosition + window.innerHeight - 50) < (getOffset(this.$products).bottom + this.fixedLocationAdjustment)) {
                        this.$locationScrollPosition.classList.add('is--fixed');
                        this.$locationInput.classList.add('is--fixed');
                        this.$submitButton.classList.add('is--inverted');
                        this.updateUsercentrics();
                        console.log('add');

                        if ($pac) {
                            $pac.classList.add('is--fixed');
                            this.positionPacContainer($pac, true);
                        }
                    }
                } else {
                    if (window.scrollPosition <= (getOffset(this.$locationScrollPosition).top - 100) || (window.scrollPosition + window.innerHeight - 50) > (getOffset(this.$products).bottom + this.fixedLocationAdjustment)) {
                        this.$locationScrollPosition.classList.remove('is--fixed');
                        this.$locationInput.classList.remove('is--fixed');
                        this.$submitButton.classList.remove('is--inverted');
                        this.updateUsercentrics();
                        console.log('remove');

                        if ($pac) {
                            $pac.classList.remove('is--fixed');
                            this.positionPacContainer($pac, false);
                        }
                    }
                }
            }
        });

        this.$storeFinderMobileListSwitch.addEventListener('change', () => {
            if (this.$storeFinderMobileListSwitch.checked) {
                window.storeSlider.update();
            }
        });
    }

    setStoreProducts (storeProducts, storeId) {
        const $store = this.$storeResult.querySelector('[data-store-id="' + storeId + '"]');
        const $results = $store.querySelector('[data-store-finder-products-container]');

        const $listTab = document.getElementById('storefinder-list');

        if ($listTab) {
            $listTab.checked = true;
        }

        const results = createElementFromHTML(storeProducts);
        $results.appendChild(results);
        $results.style.height = $results.firstElementChild.clientHeight + 'px';

        setTimeout(() => {
            $results.style.height = 'auto';

            window.storeSlider.slides.forEach((slide, index) => {
                if (parseInt(slide.getAttribute('data-store-id')) === parseInt(storeId)) {
                    window.storeSlider.slideTo(index);
                    window.storeSlider.update();
                }
            });
        }, 750);
    }

    getStoreProducts (storeId, handelspartnerId, callback) {
        if (handelspartnerId) {
            new AjaxLoad(this.$storeFinderStoreUrl +  '?productIds=' + this.selectedProducts +  '&mainCategories=' + this.selectedCategories + '&handelspartnerId=' + handelspartnerId, { // eslint-disable-line
                requestMethod: 'GET',
                onSuccess: (result) => {
                    // const data = JSON.parse(result);

                    this.activeRequest = false;

                    callback(result);
                }
            });
        }
    }

    checkForAutocompleteResults () {
        const $pac = document.querySelector('.pac-container');
        let waitTimer = 1;

        if ($pac) {
            const waitInterval = setInterval(() => {
                if (window.getComputedStyle($pac).display !== 'none') {
                    this.$locationInput.parentElement.classList.add('has--focus');
                    this.positionPacContainer($pac, this.$locationScrollPosition.classList.contains('is--fixed'));
                    clearInterval(waitInterval);
                } else {
                    this.$locationInput.parentElement.classList.remove('has--focus');
                }

                waitTimer++;
                if (waitTimer >= 200) {
                    clearInterval(waitInterval);
                }
            }, 1);
        } else {
            this.$locationInput.parentElement.classList.remove('has--focus');
        }
    }

    checkBeforeSubmit (event) {
        this.checkSelectedProducts();

        if (encodeURI(this.$locationInput.value) !== this.lastLocationValue || this.foundLocation === false) {
            if (document.querySelectorAll('.pac-item').length > 0) {
                this.selectFirstLocation(true);
            } else {
                if (!this.$productSelectionSwitch.checked) {
                    this.ajaxRequest(this.$form.getAttribute('action'), true);
                }
            }
        } else {
            if (event) {
                if (window.innerWidth <= 1024 && event.isTrusted) {
                    this.autoScroll = true;
                }
            }

            this.ajaxRequest(this.$form.getAttribute('action'), true);
            this.autoscroll = false;
        }
    }

    selectFirstLocation (withEnter) {
        let evt;
        if (document.documentElement.classList.contains('ie11')) {
            evt = document.createEvent('KeyboardEvent');
            evt.initKeyboardEvent('keydown', true, false, window, 'Down', 0, 0, 0, 0);
            this.$locationInput.dispatchEvent(evt);

            if (withEnter) {
                evt.initKeyboardEvent('Enter', true, false, window, 'Enter', 0, 0, 0, 0);
                this.$locationInput.dispatchEvent(evt);
            }
        } else if (document.documentElement.classList.contains('no-csshover')) {
            const Event1 = new Event('keydown');
            Event1.code = 'ArrowDown';
            Event1.key = 'ArrowDown';
            Event1.keyCode = 40;
            google.maps.event.trigger(this.$locationInput, 'keydown', Event1); // eslint-disable-line

            if (withEnter) {
                const Event2 = new Event('keydown');
                Event2.code = 'Enter';
                Event2.key = 'Enter';
                Event2.keyCode = 13;
                Event2.triggered = true;
                google.maps.event.trigger(this.$locationInput, 'keydown', Event2); // eslint-disable-line
            }
        } else {
            evt = new KeyboardEvent('keydown', { keyCode: 40, which: 40, code: 'ArrowDown' });
            this.$locationInput.dispatchEvent(evt);

            if (withEnter) {
                this.$locationInput.dispatchEvent(new KeyboardEvent('keydown', { keyCode: 13, which: 13, code: 'Enter' }));
            }
        }
    }

    ajaxRequest (url, scroll) {
        console.log('scroll', scroll);
        if (this.activeRequest === false) {
            this.checkSelectedProducts();
            this.checkSelectedCategories();

            this.activeRequest = true;

            const formData = new FormData(this.$form);
            if (this.selectedProducts !== '') {
                formData.append('productIds', this.selectedProducts);
            }

            if (this.selectedCategories !== '') {
                formData.append('mainCategories', this.selectedCategories);
            }

            formData.append('radius', this.$radius.value);

            new AjaxLoad(url, { // eslint-disable-line
                requestMethod: 'POST',
                requestData: formData,
                onSuccess: (result) => {
                    const data = JSON.parse(result);

                    this.$productSelectionSwitch.checked = true;
                    this.$locationScrollPosition.classList.remove('is--fixed');

                    if (data.marker) {
                        this.$storeFinder.classList.remove('has--no-results');
                        this.setResult(data, scroll);
                        this.setSelectedProducts(data);
                    }

                    // else {
                    //     this.$storeFinder.classList.add('has--no-results');
                    //     this.setNoResult(data);
                    // }

                    this.activeRequest = false;
                    this.$submitButton.classList.add('is--disabled');
                    this.$submitButton.classList.remove('is--inverted');
                }
            });
        }
    }

    setResult (data, scroll) {
        const markers = JSON.parse(data.marker);

        this.updateUsercentrics();

        if (data.resultText) {
            this.setStoreResultText(data.resultText);
        }

        if (data.productNotification) {
            this.setProductNotification(data.productNotification);
        } else if (this.$productNotification) {
            this.$productNotification.remove();
        }

        const storeResults = createElementFromHTML(data.result);

        this.destroySlider(window.storeSlider, () => {
            const $swiperWrapper = this.$storeResult.querySelector('.swiper-wrapper');

            if ($swiperWrapper) {
                $swiperWrapper.remove();
            }

            if (storeResults.length > 1) {
                storeResults.forEach((item) => {
                    this.$storeResult.appendChild(item);
                });
            } else {
                this.$storeResult.appendChild(storeResults);
            }
        });

        if (scroll) {
            this.scrollTo(this.$resultAnchor);
        }

        this.initSlider();

        this.storeMap.renewMarker(markers);
        this.storeMap.setVisualRadius(this.inputLAT.value, this.inputLNG.value, this.$radius.value);
    }

    setSelectedProducts (data) {
        const selectedProducts = createElementFromHTML(data.selectedProducts);

        this.destroySlider(this.productSelectionsSlider, () => {
            const $swiperWrapper = this.$productSelections.querySelector('.swiper-wrapper');
            $swiperWrapper.innerHTML = '';

            if (selectedProducts) {
                if (selectedProducts.length > 1) {
                    selectedProducts.forEach((element) => {
                        $swiperWrapper.appendChild(element);
                    });
                } else {
                    $swiperWrapper.appendChild(selectedProducts);
                }
            }
        });

        if (selectedProducts) {
            this.$selections.classList.add('is--visible');
            this.$selectionActions.classList.remove('is--visible');
            this.initSelectedProductsSlider();
        } else {
            this.$selections.classList.remove('is--visible');
            this.$selectionActions.classList.add('is--visible');
        }
    }

    setStoreResultText (resultText) {
        this.$storeResultTextWrapper.classList.remove('is--hidden');
        this.$storeResultText.innerHTML = resultText;
    }

    setProductNotification (productNotification) {
        this.$storeResult.innerHTML = productNotification;

        const contentUpdateEvent = new CustomEvent('content.loaded', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: this.$storeResult
            }
        });

        window.setTimeout(() => {
            window.dispatchEvent(contentUpdateEvent);
        }, 500);
    }
}

export { StoreFinder };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;
    console.log('storefinder');

    if ($context) {
        const $storeFinder = $context.querySelector('[data-store-finder="root"]');
        if ($storeFinder) {
            const stores = new StoreFinder($storeFinder);
            stores.initialize();
        }
    }
});
